export const TABLE_TOUCH_TARGET_SIZE = 44;

export const HIDE_BELOW_KEYS = ['sm', 'md', 'lg', 'xl'];

const isValidHideBelow = (value) => HIDE_BELOW_KEYS.includes(value);

const collectHideBelowKeys = (labels) => {
  if (!Array.isArray(labels)) return [];

  return labels.reduce((accumulator, label) => {
    if (!isValidHideBelow(label?.hideBelow)) return accumulator;
    return accumulator.includes(label.hideBelow) ? accumulator : [...accumulator, label.hideBelow];
  }, []);
};

export const hasHiddenColumns = (labels) => collectHideBelowKeys(labels).length > 0;

/**
 * Breakpoint em que a tabela recupera todas as colunas — é a partir dele que o
 * `minWidth` e o `tableLayout` originais voltam a valer.
 */
export const resolveRestoreBreakpoint = (labels) => {
  const declared = collectHideBelowKeys(labels);

  if (declared.length === 0) return null;

  return HIDE_BELOW_KEYS.reduce(
    (widest, key) => (declared.includes(key) ? key : widest),
    declared[0]
  );
};

export const resolveHeadCellDisplaySx = (hideBelow) => {
  if (!isValidHideBelow(hideBelow)) return undefined;

  return { xs: 'none', [hideBelow]: 'table-cell' };
};

/**
 * O índice da célula do corpo é derivado aqui, somando o deslocamento da célula
 * de checkbox das tabelas `selectable`. O consumidor nunca escreve `nth-of-type`.
 */
export const resolveHiddenBodyColumnsSx = (labels, { selectable = false } = {}) => {
  if (!Array.isArray(labels)) return null;

  const checkboxOffset = selectable ? 1 : 0;

  const rules = labels.reduce((accumulator, label, index) => {
    if (!isValidHideBelow(label?.hideBelow)) return accumulator;

    const position = index + 1 + checkboxOffset;

    return {
      ...accumulator,
      [`& > tbody > tr > td:nth-of-type(${position})`]: {
        display: { xs: 'none', [label.hideBelow]: 'table-cell' },
      },
    };
  }, {});

  return Object.keys(rules).length > 0 ? rules : null;
};

export const resolveResponsiveTableSx = ({
  labels,
  minWidth,
  layout,
  touchTargetSize = TABLE_TOUCH_TARGET_SIZE,
}) => {
  const restoreAt = resolveRestoreBreakpoint(labels);

  if (!restoreAt) return null;

  return {
    minWidth: { xs: 'auto', [restoreAt]: minWidth },
    tableLayout: { xs: 'fixed', [restoreAt]: layout },
    '& th, & td': {
      px: { xs: 1.5, sm: 2 },
      overflowWrap: { xs: 'anywhere', [restoreAt]: 'normal' },
    },
    '& > tbody > tr > td .MuiIconButton-root': {
      minWidth: { xs: touchTargetSize, [restoreAt]: 'auto' },
      minHeight: { xs: touchTargetSize, [restoreAt]: 'auto' },
    },
  };
};

// Coluna de ações: sem rótulo no card, e alinhada à direita.
const isActionsColumn = (label) => !label?.label || /^actions?$/.test(label?.id || '');

/**
 * Abaixo de `cardsBelow` a tabela vira uma lista de cards: um `tr` por registro e cada
 * `td` numa linha "rótulo · valor", com o rótulo vindo de `labels` por `nth-of-type`, o
 * mesmo caminho de `resolveHiddenBodyColumnsSx`. O consumidor não muda o `renderRow`.
 *
 * O `td` é um grid de duas colunas, não um flex: célula com mais de um filho (nome, e-mail
 * e produto na mesma célula) empilha os filhos na coluna do valor, e o flex os espalharia
 * pela linha do card.
 *
 * Coluna com `hideBelow` continua fora do card, como já ficava fora da tabela compacta. O
 * `thead` some, e com ele a ordenação no celular. Célula vazia some. A linha que usa uma
 * coluna para outro dado troca o rótulo da célula com `data-label` (vazio tira o rótulo).
 */
export const resolveCardsSx = ({
  labels,
  cardsBelow,
  selectable = false,
  touchTargetSize = TABLE_TOUCH_TARGET_SIZE,
}) => {
  if (!isValidHideBelow(cardsBelow) || !Array.isArray(labels)) return null;

  const checkboxOffset = selectable ? 1 : 0;

  const cellRules = labels.reduce((accumulator, label, index) => {
    if (isValidHideBelow(label?.hideBelow)) return accumulator;

    const selector = `& > tbody > tr > td:nth-of-type(${index + 1 + checkboxOffset})`;

    if (isActionsColumn(label)) {
      return { ...accumulator, [selector]: { display: 'flex', justifyContent: 'flex-end' } };
    }

    if (typeof label?.label !== 'string') return accumulator;

    return {
      ...accumulator,
      [`${selector}::before`]: { content: JSON.stringify(label.label) },
    };
  }, {});

  return (theme) => ({
    [theme.breakpoints.down(cardsBelow)]: {
      display: 'block',
      width: '100%',
      minWidth: 'auto',
      tableLayout: 'auto',
      '& > thead': { display: 'none' },
      '& > tbody': {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.5),
        padding: theme.spacing(1, 2, 2),
      },
      '& > tbody:empty': { display: 'none' },
      '& > tbody > tr': {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
        padding: theme.spacing(1.5, 2),
        border: `1px solid ${theme.palette.divider}`,
        // Número aqui é múltiplo de `shape.borderRadius` (8): 1.5 dá 12px, e 12 daria 96px.
        borderRadius: 1.5,
      },
      '& > tbody > tr > td': {
        display: 'grid',
        gridTemplateColumns: 'auto minmax(0, 1fr)',
        alignItems: 'center',
        justifyItems: 'end',
        columnGap: theme.spacing(2),
        rowGap: theme.spacing(0.25),
        width: 'auto',
        minWidth: 0,
        padding: 0,
        border: 0,
        whiteSpace: 'normal',
        overflowWrap: 'anywhere',
        textAlign: 'right',
      },
      '& > tbody > tr > td > *': { gridColumn: 2, minWidth: 0, maxWidth: '100%' },
      '& > tbody > tr > td::before': {
        ...theme.typography.caption,
        gridColumn: 1,
        justifySelf: 'start',
        color: theme.palette.text.secondary,
        textAlign: 'left',
        // O cabeçalho ordenável capitaliza o rótulo (TableHeadCustom), e o card mostra o mesmo.
        textTransform: 'capitalize',
      },
      ...(selectable && {
        '& > tbody > tr > td:first-of-type': { display: 'flex', justifyContent: 'flex-start' },
      }),
      '& > tbody > tr > td .MuiIconButton-root': {
        minWidth: touchTargetSize,
        minHeight: touchTargetSize,
      },
      ...cellRules,
      // As duas regras abaixo têm a mesma especificidade das regras por coluna e precisam
      // vir depois delas: o `data-label` troca o rótulo da coluna, e a célula vazia some
      // mesmo na coluna de ações, que liga `display: flex`.
      '& > tbody > tr > td[data-label]::before': { content: 'attr(data-label)' },
      '& > tbody > tr > td:empty': { display: 'none' },
    },
  });
};
